<template>
  <zeit-prompt v-if="showPrompt" :cardClasses="['flex', 'flex-col']" maxWidth="max-w-2xl" @close="dismiss">
    <template #title>Hinweis zu Ihrem Testzeitraum</template>

    <div class="mt-4 space-y-5 text-sm leading-6">
      <p>
        Ihr Testzeitraum von TimeSec Pro läuft
        <strong>{{ remainingDays === 1 ? 'in einem Tag' : `in ${remainingDays} Tagen` }}</strong>
        ab. Um nahtlos weiterarbeiten zu können, haben Sie folgende Auswahlmöglichkeiten:
      </p>

      <ol class="list-decimal space-y-4 pl-5">
        <li>
          <strong>Rechnungsdaten hinterlegen: </strong>
          <a :href="billingUrl" target="_blank" rel="noopener noreferrer" class="font-semibold text-primary-dark underline" @click="dismiss">Hinterlegen Sie Ihre Rechnungsdaten</a>
          und gewünschten Starttermin.
        </li>
        <li>
          <strong>Testzeitraum verlängern:</strong>
          Sie benötigen noch etwas mehr Zeit zum Testen? Kontaktieren Sie uns einfach.
        </li>
        <li>
          <strong>Erstmal pausieren:</strong>
          Wenn Sie nichts tun, wird Ihr Account nach Ablauf des Testzeitraumes automatisch auf die kostenfreie Free-Version heruntergestuft und die verfügbare Nutzerzahl auf 5 Personen reduziert. Ihre bisherigen Daten bleiben dabei erhalten.
        </li>
      </ol>

      <p>
        Wir unterstützen Sie gerne bei allen weiteren Fragen und der Einrichtung eines individuellen Exportes Ihrer Arbeitszeiten (z.B. für die Lohnbuchhaltung oder Projektauswertung).
      </p>
      <p>
        Bei Fragen kontaktieren Sie uns
        <a href="https://app.timesec.de/support/" target="_blank" rel="noopener noreferrer" class="font-semibold text-primary-dark underline" @click="dismiss">hier</a>
        oder
        <a href="https://calendly.com/jan-moeller-timesec/30min" target="_blank" rel="noopener noreferrer" class="font-semibold text-primary-dark underline" @click="dismiss">buchen Sie direkt einen Termin</a>.
      </p>
      <p>
        Wir sind auch gerne persönlich für Sie unter
        <a href="tel:+493030015491" class="whitespace-nowrap font-semibold" @click="dismiss">+49 30 30015491</a>
        zu erreichen.
      </p>
    </div>

    <div class="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
      <zeit-button color="zinc" fill="clear" @click="dismiss">Schließen</zeit-button>
      <zeit-button :href="billingUrl" target="_blank" rel="noopener noreferrer" class="!text-zinc-900" @click="dismiss">Rechnungsdaten hinterlegen</zeit-button>
    </div>
  </zeit-prompt>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import moment from 'moment';
import ZeitPrompt from '@/components/ui/ZeitPrompt.vue';
import ZeitButton from '@/components/ui/v2/ZeitButton.vue';
import { session } from '@/globals/session';
import { refreshSessionIfNeeded } from '@/globals/session-refresh';

const billingUrl = 'https://forms.gle/g6zs3Jwj97Tm1ruH8';
const showPrompt = ref(false);
const remainingDays = ref(0);
const activeReminderKey = ref<string>();
const seenReminders = new Set<string>();
let updateId = 0;

function getReminder() {
  const institution = session.institution;
  const account = session.account;

  if (!institution || !account || account.role !== 'admin') return;
  // Older TimeSec institutions still use the legacy backend brand name.
  if (!['timesec', 'arbeitszeitregister'].includes(institution.brand)) return;
  if (!institution.plan || institution.plan.endsWith('_free') || !institution.trial_until) return;

  const trialEnd = moment(institution.trial_until, moment.ISO_8601, true);
  if (!trialEnd.isValid()) return;
  const days = trialEnd.startOf('day').diff(moment().startOf('day'), 'days');
  if (days < 1 || days > 10) return;

  // Only the latest reached milestone applies; older reminders are not queued.
  const milestone = days <= 1 ? 1 : days <= 5 ? 5 : 10;
  return {
    days,
    key: `timesecTrialPrompt__${institution.id}__${account.id}__${trialEnd.format('YYYY-MM-DD')}__${milestone}`,
  };
}

async function updatePrompt() {
  const currentUpdate = ++updateId;
  const fresh = await refreshSessionIfNeeded();
  if (currentUpdate !== updateId) return;
  if (!fresh) {
    showPrompt.value = false;
    activeReminderKey.value = undefined;
    return;
  }
  const reminder = getReminder();
  if (!reminder) {
    showPrompt.value = false;
    activeReminderKey.value = undefined;
    return;
  }

  remainingDays.value = reminder.days;
  // Recording an impression must not close a dialog that is still being read.
  if (showPrompt.value && activeReminderKey.value === reminder.key) return;
  activeReminderKey.value = reminder.key;
  let seen = seenReminders.has(reminder.key);
  try {
    seen ||= localStorage.getItem(reminder.key) === 'seen';
  } catch {
    // Fall back to impressions recorded during this app session.
  }
  showPrompt.value = !seen;
}

function markSeen() {
  if (!showPrompt.value || !activeReminderKey.value) return;
  seenReminders.add(activeReminderKey.value);
  try {
    localStorage.setItem(activeReminderKey.value, 'seen');
  } catch {
    // The in-memory record still prevents repeats when storage is unavailable.
  }
}

// Wait for rendering so transient session changes do not consume an unseen reminder.
watch([showPrompt, activeReminderKey], markSeen, { flush: 'post' });

function dismiss() {
  markSeen();
  showPrompt.value = false;
}

onMounted(() => {
  session.on('change', updatePrompt);
  session.on('reset', updatePrompt);
  session.on('resume', updatePrompt);
  updatePrompt();
});

onBeforeUnmount(() => {
  updateId++;
  session.off('change', updatePrompt);
  session.off('reset', updatePrompt);
  session.off('resume', updatePrompt);
});
</script>
